import * as React from "react";

import { Checkbox } from "@/components/ui/checkbox";
import type { BusinessReportPrompt } from "@/modules/snapshots/businessReports.types";

export type SummaryColumn = Pick<BusinessReportPrompt, "id" | "name" | "order">;

interface Props {
  columns: SummaryColumn[];
  selectedIds: ReadonlySet<string>;
  onSelectedIdsChange: (selectedIds: ReadonlySet<string>) => void;
  disabled: boolean;
}

const FIELD_ID = "supplier-summary-collection-columns";

/** One checkbox per template column, each running that column's prompt, plus "Select All". */
export const SummaryColumnsField: React.FC<Props> = ({ columns, selectedIds, onSelectedIdsChange, disabled }) => {
  const allSelected = selectedIds.size === columns.length;
  const selectAllState = allSelected ? true : selectedIds.size > 0 ? "indeterminate" : false;

  const toggleColumn = (columnId: string, checked: boolean) => {
    const next = new Set(selectedIds);
    if (checked) next.add(columnId);
    else next.delete(columnId);
    onSelectedIdsChange(next);
  };

  return (
    <fieldset className="text-sm" aria-describedby={`${FIELD_ID}-help`}>
      <legend className="font-medium">Columns to generate</legend>
      <p id={`${FIELD_ID}-help`} className="text-slate-500">
        Unselected columns keep their current value.
      </p>
      <div className="mt-2 flex items-center gap-2">
        <Checkbox
          id={`${FIELD_ID}-all`}
          checked={selectAllState}
          disabled={disabled}
          onCheckedChange={checked =>
            onSelectedIdsChange(new Set(checked === true ? columns.map(column => column.id) : []))
          }
        />
        <label htmlFor={`${FIELD_ID}-all`} className="font-medium">
          Select All
        </label>
      </div>
      <div role="group" aria-label="Columns" className="mt-1 max-h-60 space-y-1 overflow-y-auto pl-6">
        {columns.map(column => {
          const id = `${FIELD_ID}-${column.id}`;
          return (
            <div key={column.id} className="flex items-center gap-2">
              <Checkbox
                id={id}
                checked={selectedIds.has(column.id)}
                disabled={disabled}
                onCheckedChange={checked => toggleColumn(column.id, checked === true)}
              />
              <label htmlFor={id}>{column.name}</label>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
};
